import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '@/shared/components/ui';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';

type GuideGoLiveSetupBannerProps = {
  /** Compact variant for pages that already focus on pricing */
  compact?: boolean;
};

export function GuideGoLiveSetupBanner({ compact = false }: GuideGoLiveSetupBannerProps) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: compact ? 1.5 : 2,
        p: compact ? { xs: 2, sm: 2.25 } : { xs: 2.5, sm: 3 },
        mb: compact ? 2.5 : 0,
        borderRadius: (t) => t.radii.lg,
        border: '1px solid',
        borderColor: alpha(theme.palette.secondary.main, 0.35),
        bgcolor: alpha(theme.palette.secondary.main, 0.08),
        backgroundImage: `
          radial-gradient(ellipse 70% 80% at 100% 0%, ${alpha(theme.palette.secondary.main, 0.14)} 0%, transparent 55%),
          radial-gradient(ellipse 50% 60% at 0% 100%, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 50%)
        `,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, minWidth: 0 }}>
        <Box
          sx={(theme) => ({
            width: compact ? 40 : 48,
            height: compact ? 40 : 48,
            borderRadius: (t) => t.radii.md,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: alpha(theme.palette.secondary.main, 0.18),
            color: 'secondary.dark',
          })}
        >
          <AttachMoneyOutlinedIcon sx={{ fontSize: compact ? 22 : 26 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={compact ? 'subtitle1' : 'h6'}
            fontWeight={800}
            color="textColors.heading"
            sx={{ letterSpacing: '-0.01em', lineHeight: 1.3 }}
          >
            You&apos;re approved — add pricing to go live
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, lineHeight: 1.6, maxWidth: 480 }}
          >
            {compact
              ? 'Set rates above 0 so travelers can find and book you in search.'
              : 'Travelers can’t find or book you until you set your rates. Add hourly and full-day pricing to appear in search.'}
          </Typography>
        </Box>
      </Box>

      {!compact && (
        <Button
          component={RouterLink}
          to={GUIDE_ROUTES.PRICING}
          intent="primary-gold"
          size="large"
          endIcon={<ArrowForwardIcon />}
          disableMotion
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'stretch', sm: 'center' },
            fontWeight: 700,
            px: 2.5,
          }}
        >
          Set pricing
        </Button>
      )}
    </Box>
  );
}
